import { Fragment, ReactNode } from "react";
import { tokenizeInline } from "@/utils/messageMarkdown";

/**
 * Renders a message `body` — the counterpart to RichMessageInput.
 *
 * The composer sends its formatting as the lightweight markdown described in
 * utils/messageMarkdown, so a bubble that printed the string raw would show
 * `~~this~~` instead of struck-through text. This is deliberately a separate
 * renderer from FormattedText rather than an extension of it: FormattedText is
 * used in ~50 places for authored copy (bios, program descriptions), and
 * teaching it italics and strikethrough would change how every one of those
 * renders any stray `_` or `~~`.
 *
 * Nothing here uses dangerouslySetInnerHTML — message bodies are other users'
 * input, so the markdown is turned into React elements and hrefs are validated
 * during tokenizing.
 */

interface MessageBodyProps {
  text?: string | null;
  className?: string;
}

const BULLET_RE = /^\s*[-*]\s+/;
const FENCE_RE = /^\s*```/;

type Block =
  | { kind: "code"; lines: string[] }
  | { kind: "list"; items: string[] }
  | { kind: "text"; lines: string[] };

/**
 * Close and reopen a mark that spans a line break, so it still renders.
 *
 * WHY THIS IS NEEDED
 * ------------------
 * Inline marks are tokenized PER LINE (see the render below — each line is its
 * own run, separated by <br/>). A `**` that opens on one line and closes on a
 * later one therefore never pairs, and both markers print literally. Anything
 * pasted from an LLM hits this constantly:
 *
 *     **AI transformation is not just a technology journey.
 *
 *     It is a business, architecture, and operating-model transformation.**
 *
 * rendered with four visible asterisks and no bold at all.
 *
 * So before blocking, a span left open at a line break is closed there and
 * reopened on the next non-blank line. Blank lines are stepped over rather
 * than wrapped — they are block separators, and `****` is not a thing.
 *
 * ONLY WHEN THE MARKERS BALANCE.
 * A file with an odd number of `**` has a stray one; treating that as an
 * opener would bold everything after it to the end of the post. In that case
 * this does nothing and the marker prints literally, which is what a stray
 * marker should do.
 *
 * `_` is deliberately excluded: a single underscore is ordinary in
 * identifiers, file names and URLs, and pairing them across lines would
 * italicise text nobody marked up. Double-character marks do not have that
 * problem.
 */
const SPANNING_MARKS = ["**", "~~"] as const;

function balanceMarksAcrossLines(body: string): string {
  const lines = body.split("\n");

  for (const mark of SPANNING_MARKS) {
    // Count outside fenced code, where markers are literal text.
    let fenced = false;
    let total = 0;
    for (const line of lines) {
      if (FENCE_RE.test(line)) {
        fenced = !fenced;
        continue;
      }
      if (!fenced) total += line.split(mark).length - 1;
    }
    if (total === 0 || total % 2 !== 0) continue;

    fenced = false;
    let open = false;
    for (let i = 0; i < lines.length; i += 1) {
      if (FENCE_RE.test(lines[i])) {
        fenced = !fenced;
        continue;
      }
      if (fenced || !lines[i].trim()) continue;

      const count = lines[i].split(mark).length - 1;
      // Annotated because `open` is assigned from this below, and TS cannot
      // infer a type it is defining in terms of itself.
      const closesHere: boolean = open !== (count % 2 === 1);
      if (open) lines[i] = mark + lines[i];
      if (closesHere) lines[i] = lines[i] + mark;
      open = closesHere;
    }
  }

  return lines.join("\n");
}

function toBlocks(body: string): Block[] {
  const lines = balanceMarksAcrossLines(body).split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    if (FENCE_RE.test(lines[i])) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !FENCE_RE.test(lines[i])) {
        code.push(lines[i]);
        i += 1;
      }
      i += 1; // closing fence
      blocks.push({ kind: "code", lines: code });
      continue;
    }

    if (BULLET_RE.test(lines[i])) {
      const items: string[] = [];
      while (i < lines.length && BULLET_RE.test(lines[i])) {
        items.push(lines[i].replace(BULLET_RE, ""));
        i += 1;
      }
      blocks.push({ kind: "list", items });
      continue;
    }

    const text: string[] = [];
    while (i < lines.length && !FENCE_RE.test(lines[i]) && !BULLET_RE.test(lines[i])) {
      text.push(lines[i]);
      i += 1;
    }
    // The blank line markdown puts either side of a list or a code block is a
    // separator, not content — rendered as-is it would show as a stray gap in
    // the bubble. Blank lines *between* two lines of text are kept.
    while (text.length && !text[0].trim()) text.shift();
    while (text.length && !text[text.length - 1].trim()) text.pop();
    if (text.length) blocks.push({ kind: "text", lines: text });
  }

  return blocks;
}

function renderInline(source: string, keyPrefix: string, links = true): ReactNode[] {
  return tokenizeInline(source, { links }).map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    // Marks nest (**_both_**), so their contents recurse rather than being
    // printed as-is.
    switch (token.kind) {
      case "bold":
        return <strong key={key}>{renderInline(token.content, key, links)}</strong>;
      case "italic":
        return <em key={key}>{renderInline(token.content, key, links)}</em>;
      case "strike":
        return <s key={key}>{renderInline(token.content, key, links)}</s>;
      case "code":
        return (
          <code key={key} className="msg-inline-code">
            {token.content}
          </code>
        );
      case "link":
        return (
          <a
            key={key}
            href={token.href}
            target="_blank"
            rel="noopener noreferrer"
            className="msg-body-link"
          >
            {/* Links don't nest: a bare URL's label is the URL itself, so
                recursing with linkifying still on would never terminate. */}
            {renderInline(token.content, key, false)}
          </a>
        );
      default:
        return <Fragment key={key}>{token.content}</Fragment>;
    }
  });
}

export function MessageBody({ text, className = "" }: MessageBodyProps) {
  if (!text) return null;

  return (
    <div className={`msg-body ${className}`.trim()}>
      {toBlocks(text).map((block, blockIndex) => {
        if (block.kind === "code") {
          return (
            <pre key={blockIndex} className="msg-code-block">
              <code>{block.lines.join("\n")}</code>
            </pre>
          );
        }

        if (block.kind === "list") {
          return (
            <ul key={blockIndex} className="msg-body-list">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `b${blockIndex}-i${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }

        return (
          <p key={blockIndex} className="msg-body-paragraph">
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `b${blockIndex}-l${lineIndex}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
